import { NgModule, LOCALE_ID, Injector } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';

import { AlertComponent } from './shared/components/alert';
import { JwtInterceptor, ErrorInterceptor } from './core/helpers';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { CaseModule } from './modules/case/case.module';
import { DrDateAdapter } from './shared/modules/ang-material-import/ang-material-import.module';
import { CoreModule } from './core/core.module';
import { HttpLoaderFactory, SharedModule } from './shared/shared.module';
import { DashboardModule } from './modules/dashboard/dashboard.module';
import { StoreModule } from '@ngrx/store';
import { reducers, metaReducers } from './core/reducers';
import { EffectsModule } from '@ngrx/effects';
import { AuthenticationEffects } from '@app/core/authentication/authentication.effects';
import { environment } from '../environments/environment';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';
import localeDe from '@angular/common/locales/de';
import { registerLocaleData } from '@angular/common';
import { DrI18nService, LocaleIdFactory } from './core/services/i18n/i18n.service';
import { CaseRoutingModule } from '@app/modules/case/case.routing';
import { TemplateRoutingModule } from '@app/modules/template/template.routing';
import { TemplateModule } from '@app/modules/template/template.module';
import { AuthModule } from '@app/modules/auth/auth.module';
import { SettingsModule } from '@app/modules/settings/settings.module';
import { SettingsRoutingModule } from '@app/modules/settings/settings.routing';
import { InsignRoutingModule } from '@app/modules/insign-confirm/insign-routing';
import { InsignModule } from '@app/modules/insign-confirm/insign.module';
import { LogModule } from '@app/modules/log/log.module';
import { LogRoutingModule } from '@app/modules/log/log-routing';
import { AppConfigModule } from './app-config.module';
import { PersonalModule } from '@app/modules/personal/personal.module';
import { PersonalRoutingModule } from '@app/modules/personal/personal.routing';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

export let AppInjector: Injector;

// Register German as locale
registerLocaleData(localeDe, 'de-DE');

@NgModule({
  declarations: [
    AppComponent,
    AlertComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,

    // Routing
    TemplateRoutingModule,
    CaseRoutingModule,
    PersonalRoutingModule,
    SettingsRoutingModule,
    InsignRoutingModule,
    LogRoutingModule,
    AppRoutingModule,

    // SMP modules
    CoreModule,
    SharedModule,
    AuthModule,
    DashboardModule,
    CaseModule,
    LogModule,
    TemplateModule,
    SettingsModule,
    PersonalModule,
    InsignModule,
    AppConfigModule,

    // Translation
    TranslateModule.forRoot({
      loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
      }
    }),

    // NgRx
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot([AuthenticationEffects]),
    StoreDevtoolsModule.instrument({
        maxAge: 25, // Retains last 25 states
        logOnly: environment.production, // Restrict extension to log-only mode
    })
  ],
  exports: [
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    // Setting LOCALE_ID
    { provide: LOCALE_ID, useFactory: LocaleIdFactory, deps: [DrI18nService] },
    // Setting angular material locale
    { provide: MAT_DATE_LOCALE, useFactory: LocaleIdFactory, deps: [DrI18nService] },
    // Setting angular material DateAdapter
    { provide: DateAdapter, useClass: DrDateAdapter },
    // provider used to create fake backend
    // fakeBackendProvider
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  // Injecting Dr18nService to make sure language is extracted and setting DrDateAdapter so locale can be changed dynamically.
  constructor(private drI18nService: DrI18nService, private drDateAdapter: DateAdapter<any>, private injector: Injector) {
    drI18nService.drDateAdapter = drDateAdapter as DrDateAdapter;
    AppInjector = this.injector;
  }
}



// Extensions of existing types
// for some reason ... need to be in a module file
declare global {
  interface Date {
    drDateTimeString(): string;
  }
}

Date.prototype.drDateTimeString = function(): string {
  return DrI18nService.dateTimeString(this);
};
